import { useEffect, useRef, useState, type FormEvent } from 'react';

import { api, classifyAiTaskError, type AiTaskError } from '../../services/api';
import type { AITaskResult } from '../../types';

const HELP_TOPICS = [
  { icon: '📋', label: 'Hoe stel ik toestemming in?', query: 'Hoe stel ik toestemming (consent) in voor een contact?' },
  { icon: '🤖', label: 'Hoe werkt automode?', query: 'Hoe werkt automode en wanneer stuurt de AI automatisch replies?' },
  { icon: '📤', label: 'Hoe stuur ik bulk berichten?', query: 'Hoe stuur ik een bulk bericht naar meerdere contacts?' },
  { icon: '⏰', label: 'Hoe plan ik een bericht?', query: 'Hoe plan ik een bericht in voor een later tijdstip?' },
  { icon: '🎨', label: 'Hoe werkt de learning engine?', query: 'Hoe werkt de learning engine en wat doet het met mijn stijl?' },
  { icon: '🔒', label: 'Veiligheidsregels uitleg', query: 'Wat zijn de veiligheidsregels voor automatische AI replies?' },
];

const AI_TASK_TIMEOUT_MS = 30_000;

const HelpCenter = () => {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<AITaskResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AiTaskError | null>(null);
  const isRunning = useRef(false);
  const abortController = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortController.current?.abort();
      abortController.current = null;
    };
  }, []);

  const runTask = async (content: string) => {
    const trimmed = content.trim();
    if (!trimmed || isRunning.current) return;
    isRunning.current = true;
    abortController.current?.abort();
    const controller = new AbortController();
    abortController.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), AI_TASK_TIMEOUT_MS);

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const output = await api.aiTask(
        {
          task: 'help',
          messages: [{ role: 'user', content: trimmed }],
        },
        { signal: controller.signal },
      );
      setResult(output);
    } catch (caught) {
      if (controller.signal.aborted) {
        setError({ type: 'timeout', message: 'Het verzoek is geannuleerd of heeft te lang geduurd. Probeer opnieuw.', canRetry: true });
      } else {
        setError(classifyAiTaskError(caught));
      }
    } finally {
      clearTimeout(timeoutId);
      isRunning.current = false;
      abortController.current = null;
      setLoading(false);
    }
  };

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();
    void runTask(query);
  };

  const handleTopicClick = (topicQuery: string) => {
    setQuery(topicQuery);
    void runTask(topicQuery);
  };

  const handleRetry = () => {
    if (!error?.canRetry) return;
    void runTask(query);
  };

  return (
    <section className="ai-panel help-center" aria-label="AI MessageBot Kennisbank">
      <header className="panel-header">
        <div>
          <p className="eyebrow">AI</p>
          <h2>Kennisbank</h2>
        </div>
      </header>

      <div className="help-topics">
        <h3>Snelle vragen</h3>
        <div className="help-topic-grid">
          {HELP_TOPICS.map((topic) => (
            <button
              key={topic.label}
              type="button"
              className="help-topic-btn"
              onClick={() => handleTopicClick(topic.query)}
              disabled={loading}
            >
              <span aria-hidden="true">{topic.icon}</span>
              {topic.label}
            </button>
          ))}
        </div>
      </div>

      <form className="help-center-form" onSubmit={handleSubmit}>
        <label className="sr-only" htmlFor="help-query">
          Vraag
        </label>
        <textarea
          id="help-query"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Stel een vraag over AI MessageBot, WhatsApp, klanten, instellingen…"
          rows={3}
          maxLength={2000}
          disabled={loading}
          aria-describedby="help-hint"
        />
        <p id="help-hint" className="muted small">
          De AI-assistent geeft suggesties — geen automatische WhatsApp-acties.
        </p>
        <button
          type="submit"
          className="button primary small"
          disabled={loading || !query.trim()}
        >
          {loading ? 'Denkt na…' : 'Vraag stellen'}
        </button>
      </form>

      {loading && <p className="empty-state">AI-assistent denkt na…</p>}
      {error && (
        <div className="notice error" role="alert">
          <p>{error.message}</p>
          {error.canRetry && (
            <button
              type="button"
              className="button secondary small"
              onClick={handleRetry}
              disabled={loading}
            >
              Opnieuw proberen
            </button>
          )}
        </div>
      )}
      {result && (
        <div className="ai-result">
          <p className="muted small">
            Model: {result.model}
          </p>
          <div className="ai-result-content">{result.result.content}</div>
        </div>
      )}
    </section>
  );
};

export default HelpCenter;
